import { supabase } from "../lib/supabase";
import { systemService } from "./systemService";
import { studentService } from "./studentService";
import { hostelService } from "./hostelService";

let lastHostelExpiryCheck = 0;
let hostelExpiryCheckPromise = null;

const releaseExpiredHostelReservations = async () => {
  const now = Date.now();
  if (hostelExpiryCheckPromise) return hostelExpiryCheckPromise;
  if (now - lastHostelExpiryCheck < 30_000) return null;

  lastHostelExpiryCheck = now;
  hostelExpiryCheckPromise = hostelService
    .releaseExpired()
    .catch((error) => {
      // Fee statements must still load on deployments without the ORRA
      // migration; expiry cleanup is best-effort from this read path.
      console.warn(
        "Hostel expiry cleanup is unavailable:",
        error.message || error,
      );
      return null;
    })
    .finally(() => {
      hostelExpiryCheckPromise = null;
    });

  return hostelExpiryCheckPromise;
};

const amount = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const levelNumber = (value) => {
  const match = String(value ?? "").match(/[3-7]/);
  return match ? Number(match[0]) : null;
};

const parseJsonObject = (value) => {
  if (!value) return {};
  if (typeof value === "object" && !Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch {
    return {};
  }
};

const resolveLevelTuition = (program, academicLevel) => {
  const standardFee = amount(program?.tuition_fee ?? program?.tuitionFee);
  const levelFees = parseJsonObject(
    program?.level_tuition_fees ?? program?.levelTuitionFees,
  );
  const targetLevel = levelNumber(academicLevel);

  if (!targetLevel) return standardFee;

  const matchingEntry = Object.entries(levelFees).find(
    ([key]) => levelNumber(key) === targetLevel,
  );
  const configuredFee = matchingEntry ? amount(matchingEntry[1]) : 0;
  return configuredFee > 0 ? configuredFee : standardFee;
};

const resolveUnitTuition = (unit, institution) => {
  const explicitFee = amount(unit?.tuition_fee ?? unit?.tuitionFee);
  if (explicitFee > 0) return explicitFee;

  const typeFees = parseJsonObject(institution?.unit_type_fees);
  const unitType = String(
    unit?.unit_type ?? unit?.unitType ?? unit?.classification ?? "core",
  ).toLowerCase();
  return amount(typeFees?.[unitType]?.tuition);
};

const billingMode = (institution) =>
  String(institution?.billing_mode || "program_based").toLowerCase() ===
  "unit_based"
    ? "unit_based"
    : "program_based";

const billingModeLabel = (mode) =>
  mode === "unit_based" ? "Unit-based tuition" : "Programme-based tuition";

const toProgrammeId = (student) =>
  student?.program_id || student?.program?.id || null;
const toStudentId = (student) => student?.profile_id || student?.id || null;

const statementRowsWithBalance = (transactions = [], unpostedTuition = []) =>
  [...transactions, ...unpostedTuition]
    .sort((first, second) => {
      const dateDifference =
        new Date(first.transaction_date || 0).getTime() -
        new Date(second.transaction_date || 0).getTime();
      if (dateDifference !== 0) return dateDifference;
      return (
        new Date(first.created_at || 0).getTime() -
        new Date(second.created_at || 0).getTime()
      );
    })
    .reduce((rows, transaction) => {
      const previousBalance = rows.length
        ? rows[rows.length - 1].running_balance
        : 0;
      const runningBalance =
        previousBalance +
        (Number(transaction.debit_amount) || 0) -
        (Number(transaction.credit_amount) || 0);
      rows.push({ ...transaction, running_balance: runningBalance });
      return rows;
    }, []);

/**
 * Read the same ledger and pending billing rows used by the fee statement.
 * Balances are deliberately signed: a positive value is still owed, while a
 * negative value is a credit caused by an overpayment. Keeping this in one
 * service prevents administrative student views from displaying charge-only
 * or stale student-row balances.
 */
export const getStudentFeeStatementSummary = async (student) => {
  const studentId = toStudentId(student);
  if (!studentId) {
    return {
      transactions: [],
      unpostedTuition: [],
      statementRows: [],
      totalDebit: 0,
      totalCredit: 0,
      balance: 0,
      hasTransactions: false,
    };
  }

  // This catches holds that expired while the student was away from the
  // accommodation page before the statement is assembled.
  await releaseExpiredHostelReservations();

  const [statementResult, pendingResult] = await Promise.all([
    studentService.getStudentStatement(studentId),
    // Some academic roles cannot read provisional billing requests. Posted
    // ledger rows remain authoritative for those roles, so do not discard the
    // usable statement when the optional pending lookup is denied by RLS.
    getUnpostedStatementTuition(student).catch((error) => {
      console.warn(
        "Pending fee rows are unavailable for this student view:",
        error.message || error,
      );
      return [];
    }),
  ]);

  const transactions = Array.isArray(statementResult?.data)
    ? statementResult.data
    : [];
  const unpostedTuition = Array.isArray(pendingResult) ? pendingResult : [];
  const statementRows = statementRowsWithBalance(transactions, unpostedTuition);
  const totalDebit = statementRows.reduce(
    (sum, row) => sum + (Number(row.debit_amount) || 0),
    0,
  );
  const totalCredit = statementRows.reduce(
    (sum, row) => sum + (Number(row.credit_amount) || 0),
    0,
  );

  return {
    transactions,
    unpostedTuition,
    statementRows,
    totalDebit,
    totalCredit,
    balance: statementRows.length
      ? Number(statementRows[statementRows.length - 1].running_balance)
      : 0,
    hasTransactions: statementRows.length > 0,
    error: statementResult?.error || null,
  };
};

/**
 * Fetch booked summative examinations for the signed-in student.
 * The table is protected by the student's own-row RLS policy, so we do not
 * need to rely on a particular students.id/profile_id representation here.
 * Older deployments may not have migration 20260101000044 yet; in that case
 * the fee structure should still load without examination rows.
 */
const fetchStudentSummativeExamBookings = async () => {
  const { data: bookings, error } = await supabase
    .from("summative_exam_bookings")
    .select(
      "id,unit_id,module_number,unit_type,fee_amount,booking_status,booked_at",
    )
    .eq("booking_status", "booked")
    .order("booked_at", { ascending: true });

  if (error) {
    console.warn(
      "Summative examination billing is unavailable in the fee structure:",
      error.message,
    );
    return [];
  }

  const unitIds = [
    ...new Set(
      (bookings || []).map((booking) => booking.unit_id).filter(Boolean),
    ),
  ];
  if (!unitIds.length) return bookings || [];

  const { data: units, error: unitError } = await supabase
    .from("units")
    .select("id,code,name")
    .in("id", unitIds);
  if (unitError) {
    console.warn(
      "Summative examination unit details are unavailable:",
      unitError.message,
    );
  }

  const unitById = new Map(
    (units || []).map((unit) => [String(unit.id), unit]),
  );
  return (bookings || []).map((booking) => ({
    ...booking,
    unit: unitById.get(String(booking.unit_id)) || null,
  }));
};

const fetchStudentFeeContext = async (studentId) => {
  if (!studentId) return null;
  const { data, error } = await supabase.rpc("get_student_fee_context", {
    target_student: studentId,
  });
  if (error) {
    // This RPC is added by migration_v75. Keep the direct-query fallback so
    // older environments remain usable while the migration is being applied.
    console.warn("Student fee context RPC is unavailable:", error.message);
    return null;
  }
  return data || null;
};

const fetchProgram = async (programId, fallbackProgram = null) => {
  if (!programId) return null;
  const { data, error } = await supabase
    .from("programs")
    .select("id,name,code,tuition_fee,level_tuition_fees")
    .eq("id", programId)
    .maybeSingle();
  if (!error && data) return data;

  // Programme rows are deliberately protected from direct student reads.
  // The scoped fee context is the authorised read path for the portal.
  const { data: scopedRows, error: scopedError } = await supabase.rpc(
    "get_scoped_programs",
  );
  const scopedProgram = (scopedRows || [])
    .map((row) => row.program || row)
    .find((row) => String(row.id) === String(programId));
  if (scopedProgram) return scopedProgram;
  if (fallbackProgram && String(fallbackProgram.id) === String(programId))
    return fallbackProgram;
  if (scopedError && error) throw error;
  return null;
};

const fetchProgrammeUnits = async (
  programId,
  academicLevel,
  fallbackUnits = [],
) => {
  if (!programId) return [];
  const { data, error } = await supabase
    .from("units")
    .select(
      "id,code,name,unit_type,classification,tuition_fee,program_level,module_in_level,module,is_active",
    )
    .eq("program_id", programId);
  if (error) {
    return fallbackUnits;
  }

  const targetLevel = levelNumber(academicLevel);
  return (data || []).filter((unit) => {
    if (unit.is_active === false) return false;
    return (
      !targetLevel ||
      !unit.program_level ||
      Number(unit.program_level) === targetLevel
    );
  });
};

const programmeModuleTuition = (program, academicLevel) => {
  const annualTuition = resolveLevelTuition(program, academicLevel);
  return {
    annualTuition,
    moduleTuition: annualTuition / 3,
  };
};

/**
 * Produces the annual fee-structure columns using the active billing mode.
 * Programme tuition is annual and is shown in three equal module instalments;
 * unit tuition belongs to the module where that unit is offered.
 */
export const getStudentFeeStructure = async (student) => {
  const programId = toProgrammeId(student);
  const studentId = toStudentId(student);
  const context = await fetchStudentFeeContext(studentId);
  const [institution, program, summativeExamBookings] = await Promise.all([
    systemService.getInstitutionDetails(),
    fetchProgram(programId, context?.program || student?.program),
    fetchStudentSummativeExamBookings(),
  ]);
  const resolvedInstitution = institution || context?.institution || {};
  const resolvedProgram =
    program || context?.program || student?.program || null;

  const mode = billingMode(resolvedInstitution);
  if (!programId || !resolvedProgram) {
    return {
      institution: resolvedInstitution,
      billingMode: mode,
      billingModeLabel: billingModeLabel(mode),
      baseTuition: 0,
      m1: 0,
      m2: 0,
      m3: 0,
      unitCount: 0,
      summativeExamBookings,
    };
  }

  if (mode === "program_based") {
    const { annualTuition, moduleTuition } = programmeModuleTuition(
      resolvedProgram,
      student?.academic_level,
    );
    return {
      institution: resolvedInstitution,
      program: resolvedProgram,
      billingMode: mode,
      billingModeLabel: billingModeLabel(mode),
      baseTuition: annualTuition,
      m1: moduleTuition,
      m2: moduleTuition,
      m3: moduleTuition,
      unitCount: 0,
      summativeExamBookings,
    };
  }

  const units = context?.units?.length
    ? context.units
    : await fetchProgrammeUnits(programId, student?.academic_level);
  const moduleTotals = { 1: 0, 2: 0, 3: 0 };
  units.forEach((unit) => {
    const module = Number(unit.module_in_level ?? unit.module ?? 1);
    if (moduleTotals[module] !== undefined) {
      moduleTotals[module] += resolveUnitTuition(unit, resolvedInstitution);
    }
  });

  return {
    institution: resolvedInstitution,
    program: resolvedProgram,
    billingMode: mode,
    billingModeLabel: billingModeLabel(mode),
    baseTuition: moduleTotals[1] + moduleTotals[2] + moduleTotals[3],
    m1: moduleTotals[1],
    m2: moduleTotals[2],
    m3: moduleTotals[3],
    unitCount: units.length,
    summativeExamBookings,
  };
};

const feeForBillingRequest = ({
  request,
  enrollment,
  program,
  registrations,
  institution,
  student,
}) => {
  const recordedTuition = amount(request?.base_tuition_fee);
  if (recordedTuition > 0) return recordedTuition;

  const mode =
    String(
      request?.metadata?.billing_mode || billingMode(institution),
    ).toLowerCase() === "unit_based"
      ? "unit_based"
      : "program_based";

  if (mode === "program_based") {
    const billedLevel =
      request?.metadata?.billed_level ??
      enrollment?.program_level ??
      student?.academic_level;
    return programmeModuleTuition(program, billedLevel).moduleTuition;
  }

  return registrations
    .filter(
      (registration) =>
        String(registration.enrollment_status).toLowerCase() === "enrolled",
    )
    .reduce(
      (total, registration) =>
        total + resolveUnitTuition(registration.unit, institution),
      0,
    );
};

/**
 * Returns tuition rows for completed registrations that are awaiting posting
 * to the student ledger. This keeps the consolidated report complete while
 * finance processes the billing queue and avoids duplicating posted invoices.
 */
export const getUnpostedStatementTuition = async (student) => {
  const studentId = toStudentId(student);
  const programId = toProgrammeId(student);
  if (!studentId || !programId) return [];

  const [institution, program, billingResult] = await Promise.all([
    systemService.getInstitutionDetails(),
    fetchProgram(programId, student?.program),
    supabase
      .from("student_billing_requests")
      .select(
        "id,semester_enrollment_id,status,requested_at,finance_reference,base_tuition_fee,late_reporting_fee,late_unit_registration_fee,reactivation_fee,metadata",
      )
      .eq("student_id", studentId)
      .order("requested_at", { ascending: true }),
  ]);

  const context = await fetchStudentFeeContext(studentId);
  const resolvedInstitution = institution || context?.institution || {};
  const resolvedProgram =
    program || context?.program || student?.program || null;

  if (billingResult.error) throw billingResult.error;
  // The student ledger is protected by role-specific RLS, so an academic
  // administrator may not be able to read its reference_id column directly.
  // The secured statement RPC is available to authenticated users and exposes
  // the finance document number. Billing requests use the same document
  // number, allowing us to distinguish genuinely unposted charges from rows
  // that are already represented in the statement.
  const statementResult = await studentService.getStudentStatement(studentId);
  const ledgerRows = Array.isArray(statementResult?.data)
    ? statementResult.data
    : [];
  const postedTuitionReferences = new Set(
    ledgerRows
      .filter((row) => /tuition/i.test(String(row.description || "")))
      .flatMap((row) =>
        [row.reference_id, row.doc_no]
          .filter(Boolean)
          .map((value) => String(value)),
      ),
  );
  const postedPenaltyKeys = new Set();
  ledgerRows
    .filter((row) =>
      /penalty|reactivation/i.test(String(row.description || "")),
    )
    .forEach((row) => {
      const description = String(row.description || "")
        .trim()
        .toLowerCase();
      [row.reference_id, row.doc_no].filter(Boolean).forEach((value) => {
        const reference = String(value);
        postedPenaltyKeys.add(`${reference}::${description}`);
        // A historical aggregate ledger row is the old reporting charge after
        // migration, so it also satisfies a pending reporting check.
        if (
          description === "late registration penalty" ||
          description === "late reporting / registration penalty"
        ) {
          postedPenaltyKeys.add(`${reference}::late reporting penalty`);
        }
      });
    });
  const requests = billingResult.data || [];
  if (!requests.length || !resolvedProgram) return [];

  const enrollmentIds = requests
    .map((request) => request.semester_enrollment_id)
    .filter(Boolean);
  const { data: enrollments, error: enrollmentError } = enrollmentIds.length
    ? await supabase
        .from("student_semester_enrollments")
        .select("id,program_level,module_number")
        .in("id", enrollmentIds)
    : { data: [], error: null };
  if (enrollmentError) {
    console.warn(
      "Semester enrolment details are unavailable for the fee report:",
      enrollmentError.message,
    );
  }
  const enrollmentById = new Map(
    (enrollments || []).map((enrollment) => [
      String(enrollment.id),
      enrollment,
    ]),
  );

  const { data: registrationRows, error: registrationError } =
    enrollmentIds.length
      ? await supabase
          .from("student_unit_registrations")
          .select("semester_enrollment_id,enrollment_status,unit_id")
          .in("semester_enrollment_id", enrollmentIds)
      : { data: [], error: null };
  if (registrationError) {
    console.warn(
      "Registered-unit details are unavailable for the fee report:",
      registrationError.message,
    );
  }

  const unitIds = [
    ...new Set(
      (registrationRows || []).map((row) => row.unit_id).filter(Boolean),
    ),
  ];
  const { data: units, error: unitError } = unitIds.length
    ? await supabase
        .from("units")
        .select("id,code,name,unit_type,classification,tuition_fee")
        .in("id", unitIds)
    : { data: [], error: null };
  if (unitError) {
    console.warn(
      "Unit fee details are unavailable for the fee report:",
      unitError.message,
    );
  }
  const unitById = new Map(
    (units?.length ? units : context?.units || []).map((unit) => [
      String(unit.id),
      unit,
    ]),
  );

  const registrationsByEnrollment = new Map();
  (registrationRows || []).forEach((row) => {
    const key = String(row.semester_enrollment_id);
    const collection = registrationsByEnrollment.get(key) || [];
    collection.push({
      ...row,
      unit: unitById.get(String(row.unit_id)) || null,
    });
    registrationsByEnrollment.set(key, collection);
  });

  return requests
    .filter(
      (request) => String(request.status || "").toLowerCase() !== "cancelled",
    )
    .flatMap((request) => {
      const enrollment = enrollmentById.get(
        String(request.semester_enrollment_id),
      );
      const registrations =
        registrationsByEnrollment.get(String(request.semester_enrollment_id)) ||
        [];
      const mode =
        String(
          request?.metadata?.billing_mode || billingMode(resolvedInstitution),
        ).toLowerCase() === "unit_based"
          ? "unit_based"
          : "program_based";
      const tuition = feeForBillingRequest({
        request,
        enrollment,
        program: resolvedProgram,
        registrations,
        institution: resolvedInstitution,
        student,
      });
      // Read only the split penalty columns. The legacy aggregate was
      // appended as a single pending row next to the two ledger penalties.
      const lateReportingFee = Number(
        request?.late_reporting_fee ||
          request?.metadata?.late_reporting_fee ||
          0,
      );
      const lateUnitRegistrationFee = Number(
        request?.late_unit_registration_fee ||
          request?.metadata?.late_unit_registration_fee ||
          0,
      );
      const reactivationFee = Number(
        request?.reactivation_fee || request?.metadata?.reactivation_fee || 0,
      );
      if (
        tuition <= 0 &&
        lateReportingFee <= 0 &&
        lateUnitRegistrationFee <= 0 &&
        reactivationFee <= 0
      )
        return [];

      const moduleNumber =
        request?.metadata?.billed_module ?? enrollment?.module_number;
      const base = {
        id: `pending-tuition-${request.id}`,
        transaction_date: request.requested_at,
        doc_no:
          request.finance_reference ||
          `BILL-${String(request.id).slice(0, 8).toUpperCase()}`,
        description:
          tuition > 0
            ? `Tuition Fee${moduleNumber ? ` Module ${moduleNumber}` : ""}`
            : "Enrollment charges",
        transaction_type: "invoice",
        debit_amount: tuition,
        credit_amount: 0,
        pending_posting: true,
      };
      const billingReferences = [
        request.id,
        request.finance_reference,
        `BILL-${String(request.id).slice(0, 8).toUpperCase()}`,
      ]
        .filter(Boolean)
        .map((value) => String(value));
      const hasPostedPenalty = (description) =>
        billingReferences.some((reference) =>
          postedPenaltyKeys.has(`${reference}::${description}`),
        );
      const pendingRows = [];
      if (
        tuition > 0 &&
        !billingReferences.some((reference) =>
          postedTuitionReferences.has(reference),
        )
      )
        pendingRows.push(base);
      if (lateReportingFee > 0 && !hasPostedPenalty("late reporting penalty")) {
        pendingRows.push({
          ...base,
          id: `${base.id}-late-reporting`,
          description: "Late Reporting Penalty",
          debit_amount: lateReportingFee,
        });
      }
      if (
        lateUnitRegistrationFee > 0 &&
        !hasPostedPenalty("late unit registration penalty")
      ) {
        pendingRows.push({
          ...base,
          id: `${base.id}-late-unit-registration`,
          description: "Late Unit Registration Penalty",
          debit_amount: lateUnitRegistrationFee,
        });
      }
      if (
        reactivationFee > 0 &&
        !hasPostedPenalty("semester reactivation fee")
      ) {
        pendingRows.push({
          ...base,
          id: `${base.id}-reactivation`,
          description: "Semester Reactivation Fee",
          debit_amount: reactivationFee,
        });
      }
      return pendingRows;
    })
    .filter(Boolean);
};
